import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { Button } from 'primeng/button';
import { Dialog } from 'primeng/dialog';
import { IftaLabelModule } from 'primeng/iftalabel';
import { Textarea } from 'primeng/textarea';
import { SampleState } from '../../../../../../types/sample/SampleState';
import { SampleService } from '../../../../services/sample.service';

@Component({
    selector: 'app-change-sample-state-dialog',
    imports: [
        Dialog,
        Button,
        Textarea,
        IftaLabelModule,
        FormsModule
    ],
    templateUrl: './change-sample-state-dialog.component.html',
    styleUrl: './change-sample-state-dialog.component.scss'
})
export class ChangeSampleStateDialogComponent {

    @Input() public visible: boolean = false;
    @Input() public header: string = '';
    @Input() public message: string = '';
    @Input() public sampleId: number = 0;
    @Input() public newSampleState: SampleState = SampleState.CREATED;

    @Output() public visibleChange: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output() public sampleStateChange: EventEmitter<SampleState> = new EventEmitter<SampleState>();

    public comment: string = '';
    public loading: boolean = false;

    constructor(private sampleService: SampleService, private messageService: MessageService) {
    }

    public close(): void {
        this.comment = '';
        this.visible = false;
        this.visibleChange.emit(this.visible);
    }

    public onAccept(): void {
        this.loading = true;
        switch (this.newSampleState) {
            case SampleState.PUBLISHED:
                this.sampleService.publishSample(this.sampleId, this.comment).then(() => {
                    this.messageService.add(
                        { severity: 'success', summary: 'Success', detail: 'Sample published successfully' });
                    this.sampleStateChange.emit(SampleState.PUBLISHED);
                    this.close();
                }).catch((error: any) => {
                    this.messageService.add(
                        { severity: 'error', summary: 'Failed to publish sample', detail: error.message });
                }).finally(() => {
                    this.loading = false;
                });
                break;
            case SampleState.APPROVAL_REQUESTED:
                this.sampleService.requestApproval(this.sampleId, this.comment).then(() => {
                    this.messageService.add(
                        { severity: 'success', summary: 'Success', detail: 'Approval requested successfully' });
                    this.sampleStateChange.emit(SampleState.APPROVAL_REQUESTED);
                    this.close();
                }).catch((error: any) => {
                    this.messageService.add(
                        { severity: 'error', summary: 'Failed to request approval', detail: error.message });
                }).finally(() => {
                    this.loading = false;
                });
                break;
            case SampleState.CHANGE_REQUESTED:
                this.sampleService.requestChanges(this.sampleId, this.comment).then(() => {
                    this.messageService.add(
                        { severity: 'success', summary: 'Success', detail: 'Changes requested successfully' });
                    this.sampleStateChange.emit(SampleState.CHANGE_REQUESTED);
                    this.close();
                }).catch((error: any) => {
                    this.messageService.add(
                        { severity: 'error', summary: 'Failed to request changes', detail: error.message });
                }).finally(() => {
                    this.loading = false;
                });
                break;
            case SampleState.APPROVED_BY_REVIEWER:
                this.sampleService.approveSampleByReviewer(this.sampleId, this.comment).then(() => {
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Success',
                        detail: 'Sample approved by reviewer successfully'
                    });
                    this.sampleStateChange.emit(SampleState.APPROVED_BY_REVIEWER);
                    this.close();
                }).catch((error: any) => {
                    this.messageService.add(
                        { severity: 'error', summary: 'Failed to approve sample', detail: error.message });
                }).finally(() => {
                    this.loading = false;
                });
                break;
            case SampleState.IN_PRODUCTION:
                this.sampleService.setSampleInProduction(this.sampleId, this.comment).then(() => {
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Success',
                        detail: 'Sample moved to production successfully'
                    });
                    this.sampleStateChange.emit(SampleState.IN_PRODUCTION);
                    this.close();
                }).catch((error: any) => {
                    this.messageService.add(
                        { severity: 'error', summary: 'Failed to approve sample', detail: error.message });
                }).finally(() => {
                    this.loading = false;
                });
                break;
            case SampleState.ACTIVE:
                this.sampleService.approveSampleByProcessOwner(this.sampleId, this.comment).then(() => {
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Success',
                        detail: 'Sample set active successfully'
                    });
                    this.sampleStateChange.emit(SampleState.ACTIVE);
                    this.close();
                }).catch((error: any) => {
                    this.messageService.add(
                        { severity: 'error', summary: 'Failed to approve sample', detail: error.message });
                }).finally(() => {
                    this.loading = false;
                });
                break;
            case SampleState.ARCHIVED:
                this.sampleService.archiveSample(this.sampleId, this.comment).then(() => {
                    this.messageService.add(
                        { severity: 'success', summary: 'Success', detail: 'Sample archived successfully' });
                    this.sampleStateChange.emit(SampleState.ARCHIVED);
                    this.close();
                }).catch((error: any) => {
                    this.messageService.add(
                        { severity: 'error', summary: 'Failed to archive sample', detail: error.message });
                }).finally(() => {
                    this.loading = false;
                });
                break;
            default:
                this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Invalid sample state' });
                this.loading = false;
                this.close();
                break;
        }
        this.close();
    }

    public onReject(): void {
        this.close();
    }

}
